import InvestmentSetup from '@advisor/models/store/settings/investmentSetup/index';
import AssetClass from '@advisor/models/store/settings/investmentSetup/AssetClass';
import exceptionHandler from '@advisor/util/exceptionHandler';
// eslint-disable-next-line import/extensions
import RwInvestmentService from '@advisor/rwSdk/InvestmentService.ts';

const investmentSetupState = () => new InvestmentSetup();

const getters = {};

const assetClassHierarchyActions = {
  async createAssetClass({rootState}, payload) {
    const {riaId, riaComponent} = rootState;
    const fullPayload = {
      ...payload,
      riaId,
      riaComponent,
    };
    const sdkResponse = await RwInvestmentService.createAssetClasses(fullPayload).catch((error) => {
      exceptionHandler.handleGenericException('investmentSetup.createAssetClass', error);
      throw error;
    });

    return Promise.resolve(sdkResponse.data);
  },

  async updateAssetClass({rootState}, payload) {
    const {riaId, riaComponent} = rootState;
    const fullPayload = {
      ...payload,
      riaId,
      riaComponent,
    };
    const sdkResponse = await RwInvestmentService.updateAssetClasses(fullPayload).catch((error) => {
      exceptionHandler.handleGenericException('investmentSetup.updateAssetClass', error);
      throw error;
    });

    return Promise.resolve(sdkResponse.data);
  },

  async deleteAssetClass({rootState}, assetClassId) {
    const {riaId, riaComponent} = rootState;
    const fullPayload = {
      assetClassId,
      riaId,
      riaComponent,
    };
    const sdkResponse = await RwInvestmentService.deleteAssetClasses(fullPayload).catch((error) => {
      exceptionHandler.handleGenericException('investmentSetup.deleteAssetClass', error);
      throw error;
    });

    return Promise.resolve(sdkResponse.data);
  },

  async getCustomAssetClassHierarchy({commit, rootState}) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        riaId,
        riaComponent,
        limit: 5000,
      };
      const sdkResponse = await RwInvestmentService.getAssetClassHierarchy(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CUSTOM_ASSET_CLASS_HIERARCHY', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getCustomAssetClassHierarchy', error);

      return Promise.reject(error);
    }
  },

  async getRwAssetClassHierarchy({commit, rootState}) {
    try {
      const {riaComponent} = rootState;

      const RW_RIA_ID = 0;
      const fullPayload = {
        riaId: RW_RIA_ID,
        riaComponent,
        limit: 5000,
      };

      const sdkResponse = await RwInvestmentService.getAssetClassHierarchy(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_RW_ASSET_CLASS_HIERARCHY', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getRwAssetClassHierarchy', error);

      return Promise.reject(error);
    }
  },

  async updateAssetClassHierarchy({rootState}, payload) {
    try {
      const {riaId} = rootState;
      const {assetClassId, parentAssetClassId} = payload;
      const fullPayload = {
        riaId,
        assetClassId,
        parentAssetClassId,
      };
      const sdkResponse = await RwInvestmentService.postAssetClassHierarchy(fullPayload);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.updateAssetClassHierarchy', error);

      return Promise.reject(error);
    }
  },
};

const securityClassificationActions = {
  async getCustomTickerAssetClasses({rootState, commit}) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        riaId,
        riaComponent,
        limit: 5000,
      };
      const sdkResponse = await RwInvestmentService.getAssetClasses(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CT_ASSET_CLASSES', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getCustomTickerAssetClasses', error);

      return Promise.reject(error);
    }
  },

  async getCustomHoldingsAssetClasses({rootState, commit}) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        riaId,
        riaComponent,
        limit: 5000,
      };
      const sdkResponse = await RwInvestmentService.getAssetClasses(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CH_ASSET_CLASSES', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getCustomHoldingsAssetClasses', error);

      return Promise.reject(error);
    }
  },

  async getRwAssetClasses({commit, rootState}) {
    try {
      const {riaComponent} = rootState;
      const fullPayload = {
        riaComponent,
      };
      const sdkResponse = await RwInvestmentService.getAssetClasses(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_RW_ASSET_CLASSES', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getRwAssetClasses', error);

      return Promise.reject(error);
    }
  },

  async getSecurityMasterList({rootState, commit}) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        riaId,
        riaComponent,
        limit: 5000,
      };
      const sdkResponse = await RwInvestmentService.getTickerClassifications(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_SECURITY_MASTER_LIST', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getSecurityMasterList', error);

      return Promise.reject(error);
    }
  },

  async updateSecurityDetails({rootState}, payload) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        ...payload,
        riaId,
        riaComponent,
      };
      const sdkResponse = await RwInvestmentService.insertUpdateTickerClassifications(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.updateSecurityDetails', error);

      return Promise.reject(error);
    }
  },

  async getCurrentHoldings({rootState, commit}) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        riaId,
        riaComponent,
        limit: 5000,
      };
      const sdkResponse = await RwInvestmentService.getHoldingClassifications(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_CURRENT_HOLDINGS', sdkResponse.data);

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.getCurrentHoldings', error);

      return Promise.reject(error);
    }
  },

  async updateCurrentHoldings({rootState}, payload) {
    try {
      const {riaId, riaComponent} = rootState;
      const fullPayload = {
        ...payload,
        riaId,
        riaComponent,
      };
      const sdkResponse = await RwInvestmentService.patchHoldingClassifications(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse.data);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentSetup.updateCurrentHoldings', error);

      return Promise.reject(error);
    }
  },
};

const actions = {
  ...assetClassHierarchyActions,
  ...securityClassificationActions,
};

const mutations = {
  SET_CUSTOM_ASSET_CLASS_HIERARCHY(state, arrAssetClassHierarchy) {
    let arrAssetClasses = [];

    if (arrAssetClassHierarchy.length > 0) {
      arrAssetClasses = arrAssetClassHierarchy.map((assetClass) => AssetClass.make(assetClass));
    }

    state.customAssetClassHierarchy = arrAssetClasses;
  },

  SET_RW_ASSET_CLASS_HIERARCHY(state, arrAssetClassHierarchy) {
    let arrAssetClasses = [];

    if (arrAssetClassHierarchy.length > 0) {
      arrAssetClasses = arrAssetClassHierarchy.map((assetClass) => AssetClass.make(assetClass));
    }

    state.rwAssetClassHierarchy = arrAssetClasses;
  },

  SET_CT_ASSET_CLASSES(state, arrCustomTickerAssetClasses) {
    state.securityClassification.arrCustomTickerAssetClasses = arrCustomTickerAssetClasses;
  },

  SET_CH_ASSET_CLASSES(state, arrCustomHoldingsAssetClasses) {
    state.securityClassification.arrCustomHoldingsAssetClasses = arrCustomHoldingsAssetClasses;
  },

  SET_RW_ASSET_CLASSES(state, arrRwAssetClasses) {
    state.securityClassification.arrRwAssetClasses = arrRwAssetClasses;
  },

  SET_SECURITY_MASTER_LIST(state, arrSecurityMasterList) {
    state.securityClassification.arrSecurityMasterList = arrSecurityMasterList;
  },

  SET_CURRENT_HOLDINGS(state, arrCurrentHoldings) {
    state.securityClassification.arrCurrentHoldings = arrCurrentHoldings;
  },
};

export default {
  namespaced: true,
  state: investmentSetupState,
  getters,
  actions,
  mutations,
};
